/* Deferred + domain-blocking scripts plugin */

import { defineNuxtPlugin } from "@nuxtjs/composition-api";

const SCRIPTS_CONFIG = [
  {
    type: 'external',
    src: 'https://pdpone.syfpayments.com/mpp/UniFi.js',
    attrs: { id: 'syfMPPScript', async: 'true' }
  },
  {
    type: 'inline',
    id: 'pinterest-script',
    attrs: { crossorigin: 'anonymous' },
    content: `
      !function(e){if(!window.pintrk){window.pintrk = function () {
        window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var
        n=window.pintrk;n.queue=[],n.version="3.0";var
        t=document.createElement("script");t.async=!0,t.src=e;var
        r=document.getElementsByTagName("script")[0];
        r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");
      pintrk('load', '2613467394920', {em: 'midwestphotoex@gmail.com'});
      pintrk('page');
    `,
  },
  {
    type: 'inline',
    id: 'openwidget-script',
    content: `
      window.__ow = window.__ow || {};
      window.__ow.organizationId = "2cff084d-c8b7-4f27-be43-0e8a6a58fae0";
      window.__ow.integration_name = "manual_settings";
      window.__ow.product_name = "openwidget";
      ;(function(n,t,c){function i(n){return e._h?e._h.apply(null,n):e._q.push(n)}var e={_q:[],_h:null,_v:"2.0",on:function(){i(["on",c.call(arguments)])},once:function(){i(["once",c.call(arguments)])},off:function(){i(["off",c.call(arguments)])},get:function(){if(!e._h)throw new Error("[OpenWidget] You can't use getters before load.");return i(["get",c.call(arguments)])},call:function(){i(["call",c.call(arguments)])},init:function(){var n=t.createElement("script");n.async=!0,n.type="text/javascript",n.src="https://cdn.openwidget.com/openwidget.js",t.head.appendChild(n)}};!n.__ow.asyncInit&&e.init(),n.OpenWidget=n.OpenWidget||e}(window,document,[].slice))
    `,
  }
] as const;

function injectScript(cfg: any) {
  if (cfg.type === 'external') {
    if (document.querySelector(`script[src="${cfg.src}"]`)) return;
    const s = document.createElement('script');
    s.src = cfg.src;
    if (cfg.attrs) Object.entries(cfg.attrs).forEach(([k, v]) => s.setAttribute(k, String(v)));
    s.defer = true;
    document.body.appendChild(s);
  } else if (cfg.type === 'inline') {
    if (cfg.id && document.getElementById(cfg.id)) return;
    const s = document.createElement('script');
    if (cfg.id) s.id = cfg.id;
    if (cfg.attrs) Object.entries(cfg.attrs).forEach(([k, v]) => s.setAttribute(k, String(v)));
    s.type = 'text/javascript';
    s.text = cfg.content;
    document.body.appendChild(s);
  }
}

function injectAllOnce() {
  if ((window as any).__deferredScriptsLoaded) return;
  (window as any).__deferredScriptsLoaded = true;
  SCRIPTS_CONFIG.forEach(injectScript);
}

export default defineNuxtPlugin(() => {
  if (process.server) return;

  let BLOCKED_HOSTS = ['www.shopperapproved.com'];

  const isBlockedUrl = (url: any) => {
    try {
      const u = new URL(String(url), window.location.href);
      return BLOCKED_HOSTS.includes(u.hostname);
    } catch {
      return false;
    }
  };

  // --- Patch createElement for <script> ---
  const originalCreateElement = document.createElement;
  document.createElement = function (tagName: string, ...args: any[]) {
    const el = originalCreateElement.call(this, tagName, ...args);
    if (tagName.toLowerCase() === 'script') {
      const originalSetAttribute = el.setAttribute;
      el.setAttribute = function (name: string, value: string) {
        if (name === 'src' && isBlockedUrl(value)) {
          // console.warn('Blocked script via setAttribute:', value);
          return;
        }
        return originalSetAttribute.call(this, name, value);
      };
      Object.defineProperty(el, 'src', {
        set(v: string) {
          if (isBlockedUrl(v)) {
            // console.warn('Blocked script via direct src=', v);
            return;
          }
          originalSetAttribute.call(el, 'src', v);
        },
        get() {
          return el.getAttribute('src') || '';
        },
        configurable: true
      });
    }
    return el;
  };

  // --- MutationObserver fallback ---
  const blockedScripts: Array<{ src: string; attrs: Record<string, string> }> = [];
  const scriptsObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && (node as HTMLElement).tagName === 'SCRIPT') {
          const s = node as HTMLScriptElement;
          const src = s.getAttribute('src');
          if (src && isBlockedUrl(src)) {
            const attrs: Record<string, string> = {};
            for (let i = 0; i < s.attributes.length; i++) {
              const a = s.attributes[i];
              attrs[a.name] = a.value;
            }
            blockedScripts.push({ src, attrs });
            s.remove();
            // console.warn('Blocked script via MutationObserver:', src);
          }
        }
      });
    }
  });
  scriptsObserver.observe(document.documentElement, { childList: true, subtree: true });

  // --- Fetch/XHR patch ---
  const originals: any = {
    fetch: window.fetch ? window.fetch.bind(window) : null,
    xhrOpen: XMLHttpRequest && XMLHttpRequest.prototype.open ? XMLHttpRequest.prototype.open : null,
    xhrSend: XMLHttpRequest && XMLHttpRequest.prototype.send ? XMLHttpRequest.prototype.send : null,
  };

  if (originals.fetch) {
    window.fetch = (input: RequestInfo, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : (input as Request).url;
      if (isBlockedUrl(url)) {
        // console.warn('Blocked fetch:', url);
        return new Promise<Response>(() => {});
      }
      return originals.fetch!(input as any, init);
    };
  }

  if (originals.xhrOpen && originals.xhrSend) {
    XMLHttpRequest.prototype.open = function (method: string, url: string, ...rest: any[]) {
      (this as any).__blocked__ = isBlockedUrl(url);
      return originals.xhrOpen!.apply(this, [method, url, ...rest]);
    };
    XMLHttpRequest.prototype.send = function (...args: any[]) {
      if ((this as any).__blocked__) {
        // console.warn('Blocked XHR:', (this as any).responseURL);
        return;
      }
      return originals.xhrSend!.apply(this, args as any);
    };
  }

  // --- Reveal on interaction ---
  const reveal = () => {
    BLOCKED_HOSTS = [];
    saLoadScript("https://www.shopperapproved.com/widgets/group2.0/31975.js");

    if (originals.fetch) window.fetch = originals.fetch;
    if (originals.xhrOpen) XMLHttpRequest.prototype.open = originals.xhrOpen;
    if (originals.xhrSend) XMLHttpRequest.prototype.send = originals.xhrSend;
    scriptsObserver.disconnect();

    injectAllOnce();

    blockedScripts.forEach(({ src, attrs }) => {
      const s = document.createElement('script');
      Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
      s.src = src;
      document.body.appendChild(s);
    });
    blockedScripts.length = 0;

    cleanup();
  };

    function saLoadScript(src: string) { 
        var js = window.document.createElement('script'); 
        js.src = src; 
        js.type = 'text/javascript'; 
        document.getElementsByTagName("head")[0].appendChild(js); 
    }

  const off: Record<string, EventListener> = {};
  const cleanup = () => {
    Object.entries(off).forEach(([ev, fn]) => {
      window.removeEventListener(ev, fn);
    });
  };

  const trigger = () => reveal();
  off.scroll = () => { if ((window.scrollY || 0) > 2) reveal(); };
  off.mousemove = trigger;
  off.keydown = trigger;
  off.touchstart = trigger;
  off.pointerdown = trigger;
  off.wheel = trigger;

  Object.entries(off).forEach(([ev, fn]) => {
    window.addEventListener(ev, fn, { passive: true });
  });
});
